import { useCallback, useEffect, useRef, useState } from "react";
import { getSubjectVariant } from "../../services/subjectVariantService";
import { getApiErrorCode } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";

/**
 * A variant's detail and its writes.
 * - Loads are guarded by a generation counter (as genRef in useTestBuilder): an answer that comes after a newer
 *   load or write started is dropped, so a late response never overwrites newer data.
 * - Writes run one at a time, and each answer (the detail) replaces the shown one. `pending` holds the keys of the
 *   writes queued or running, so the buttons of a place wait for its own write.
 * - A write carries the revision the user acted on (`base`: the detail's when the write was asked for, or when its
 *   dialog opened), moved on only over this page's own writes since. Writes one after another pass; one decided
 *   before someone else's change (queued behind a refused write, or asked for in a dialog left open over a reload)
 *   is refused with 409 variant_changed instead of acting on a state the user never saw.
 * - A write refused with 409 variant_changed / variant_locked or 404 reads the variant again; the error still goes
 *   to the caller, which says what happened. A variant_changed one carries `variantChange: {before, after}`, the
 *   detail the write was decided on and the one read again.
 */
export default function useVariant(uuid) {
  const [state, setState] = useState({ loading: true, error: null, detail: null });
  const [pending, setPending] = useState(() => new Set());
  const genRef = useRef(0);
  const detailRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const opsRef = useRef(0); // writes queued or running
  const ownRef = useRef(new Map()); // revision a write of this page sent → the revision it answered with

  const apply = useCallback((detail) => {
    detailRef.current = detail;
    setState({ loading: false, error: null, detail });
  }, []);

  // Resolves with the detail, or null when the answer was dropped or failed. A silent reload keeps the shown
  // detail when it fails, unless the variant is gone or out of reach.
  const load = useCallback(
    async ({ signal, silent = false } = {}) => {
      const gen = ++genRef.current;
      if (!silent) setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const detail = await getSubjectVariant(uuid, { signal });
        if (signal?.aborted || gen !== genRef.current) return null;
        apply(detail);
        return detail;
      } catch (err) {
        if (isCanceledError(err) || gen !== genRef.current) return null;
        const status = err?.response?.status;
        if (silent && status !== 403 && status !== 404) return null;
        detailRef.current = null;
        setState({ loading: false, error: err, detail: null });
        return null;
      }
    },
    [uuid, apply]
  );

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  // Someone else (another tab, the subject's other moderator) may have changed it meanwhile.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && opsRef.current === 0 && detailRef.current) load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  // The revision a write decided on `base` goes out with: `base`, moved on over this page's own writes since.
  // `since(base) !== detail.revision`: someone else changed the variant after `base`.
  const since = useCallback((base) => {
    let revision = base;
    for (let i = 0; i < 1000 && ownRef.current.has(String(revision)); i += 1) revision = ownRef.current.get(String(revision));
    return revision;
  }, []);

  // `request(revision, detail)` sends the write, with the revision to send and the detail as it is when the write's
  // turn comes, and resolves with its answer (the detail, maybe with more fields).
  const write = useCallback(
    (key, request, base = detailRef.current?.revision) => {
      opsRef.current += 1;
      setPending((prev) => new Set(prev).add(key));
      const run = async () => {
        const before = detailRef.current;
        try {
          const revision = since(base);
          const detail = await request(revision, detailRef.current);
          if (revision != null && detail?.revision != null && String(detail.revision) !== String(revision)) {
            ownRef.current.set(String(revision), detail.revision);
          }
          // Loads started before this answer are older than it.
          genRef.current += 1;
          apply(detail);
          return detail;
        } catch (err) {
          const code = getApiErrorCode(err);
          if (code === "variant_changed" || code === "variant_locked" || err?.response?.status === 404) {
            const after = await load({ silent: true });
            if (code === "variant_changed" && err && typeof err === "object") err.variantChange = { before, after };
          }
          throw err;
        } finally {
          opsRef.current -= 1;
          setPending((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }
      };
      const task = queueRef.current.then(run);
      queueRef.current = task.catch(() => {});
      return task;
    },
    [apply, load, since]
  );

  // The check a refused confirmation answered with (400 variant_incomplete {check}).
  const setCheck = useCallback((check) => {
    if (!check || !detailRef.current) return;
    apply({ ...detailRef.current, check });
  }, [apply]);

  // `reload` keeps the shown detail if it fails; `retry` (the load error's button) shows the loading and its error.
  const reload = useCallback(() => load({ silent: true }), [load]);
  const retry = useCallback(() => load(), [load]);
  // The newest detail, also before the page has rendered it (an answer handled right after a reload).
  const latest = useCallback(() => detailRef.current, []);

  return { ...state, pending, write, reload, retry, since, latest, setCheck };
}
